import React from 'react';

type OrderStatus = 'filled' | 'pending' | 'cancelled' | 'rejected';
type OrderSide = 'buy' | 'sell';

interface Activity {
  id: string;
  symbol: string;
  side: OrderSide;
  quantity: number;
  price: number;
  status: OrderStatus;
  timestamp: string;
  type: 'market' | 'limit';
}

interface RecentActivityProps {
  activities: Activity[];
  maxItems?: number;
}

const RecentActivity: React.FC<RecentActivityProps> = ({ activities, maxItems = 10 }) => {
  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    }).format(value);
  };

  const formatTimestamp = (timestamp: string) => {
    const date = new Date(timestamp);
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    if (diffDays < 7) return `${diffDays}d ago`;
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  };

  const getStatusColor = (status: OrderStatus) => {
    switch (status) {
      case 'filled':
        return '#10b981';
      case 'pending':
        return '#f59e0b';
      case 'cancelled':
        return '#6b7280';
      case 'rejected':
        return '#ef4444';
      default:
        return '#6b7280';
    }
  };

  const getStatusBgColor = (status: OrderStatus) => {
    switch (status) {
      case 'filled':
        return '#d1fae5';
      case 'pending':
        return '#fef3c7';
      case 'cancelled':
        return '#f3f4f6';
      case 'rejected':
        return '#fee2e2';
      default:
        return '#f3f4f6';
    }
  };

  const getSideColor = (side: OrderSide) => {
    return side === 'buy' ? '#3b82f6' : '#ef4444';
  };

  const displayedActivities = activities.slice(0, maxItems);

  return (
    <div style={{
      backgroundColor: 'white',
      borderRadius: '8px',
      boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
      overflow: 'hidden'
    }}>
      <div style={{
        padding: '20px 24px',
        borderBottom: '1px solid #e5e7eb',
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center'
      }}>
        <h2 style={{
          margin: 0,
          fontSize: '18px',
          fontWeight: 'bold',
          color: '#1e293b'
        }}>
          Recent Activity
        </h2>
        <span style={{
          fontSize: '12px',
          color: '#6b7280'
        }}>
          Last {displayedActivities.length} transactions
        </span>
      </div>

      {displayedActivities.length === 0 ? (
        <div style={{
          textAlign: 'center',
          padding: '40px 20px',
          color: '#9ca3af',
          fontSize: '14px'
        }}>
          No recent activity
        </div>
      ) : (
        <div>
          {displayedActivities.map((activity) => (
            <div
              key={activity.id}
              style={{
                padding: '16px 24px',
                borderBottom: '1px solid #f3f4f6',
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                transition: 'background-color 0.2s'
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.backgroundColor = '#f9fafb';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.backgroundColor = 'transparent';
              }}
            >
              {/* Left side - Symbol and details */}
              <div style={{ flex: 1 }}>
                <div style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '12px',
                  marginBottom: '4px'
                }}>
                  <span style={{
                    fontWeight: '700',
                    fontSize: '16px',
                    color: '#1e293b'
                  }}>
                    {activity.symbol}
                  </span>
                  <span style={{
                    padding: '2px 8px',
                    borderRadius: '4px',
                    fontSize: '12px',
                    fontWeight: '600',
                    color: 'white',
                    backgroundColor: getSideColor(activity.side),
                    textTransform: 'uppercase'
                  }}>
                    {activity.side}
                  </span>
                  <span style={{
                    padding: '2px 8px',
                    borderRadius: '4px',
                    fontSize: '11px',
                    fontWeight: '500',
                    color: getStatusColor(activity.status),
                    backgroundColor: getStatusBgColor(activity.status),
                    textTransform: 'capitalize'
                  }}>
                    {activity.status}
                  </span>
                </div>
                <div style={{
                  fontSize: '13px',
                  color: '#6b7280'
                }}>
                  {activity.quantity} shares @ {formatCurrency(activity.price)} ({activity.type})
                </div>
              </div>

              {/* Right side - Total and timestamp */}
              <div style={{
                textAlign: 'right',
                minWidth: '120px'
              }}>
                <div style={{
                  fontSize: '16px',
                  fontWeight: '600',
                  color: '#1e293b',
                  marginBottom: '4px'
                }}>
                  {formatCurrency(activity.quantity * activity.price)}
                </div>
                <div style={{
                  fontSize: '12px',
                  color: '#9ca3af'
                }}>
                  {formatTimestamp(activity.timestamp)}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {activities.length > maxItems && (
        <div style={{
          padding: '12px 24px',
          textAlign: 'center',
          backgroundColor: '#f9fafb',
          borderTop: '1px solid #e5e7eb'
        }}>
          <button style={{
            background: 'none',
            border: 'none',
            color: '#3b82f6',
            fontSize: '14px',
            fontWeight: '500',
            cursor: 'pointer',
            textDecoration: 'underline'
          }}>
            View all {activities.length} activities
          </button>
        </div>
      )}
    </div>
  );
};

export default RecentActivity;
